import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { unifiedWebSocketService } from '../../services/unifiedWebSocket';
import { useOptimizationStore } from '../../store/optimizationStore';

/**
 * Каркас приложения v2 (AIT-378).
 *
 * Один каркас на весь продукт: сайдбар слева с группами Tuner / Account / Admin,
 * тонкая шапка с хлебными крошками, страница справа. Гость получает тот же
 * каркас без сайдбара и с кнопкой входа в шапке.
 *
 * Заменяет Header + Sidebar + три layout-обёртки из App.tsx.
 */

/** URL чата по окружению: qa → qa.chat, dev → dev.chat, иначе прод. */
export const getChatUrl = (): string => {
  const hostname = window.location.hostname.toLowerCase();
  if (hostname.includes('qa')) return 'https://qa.chat.infiano.app';
  if (hostname.includes('dev')) return 'https://dev.chat.infiano.app';
  return 'https://chat.infiano.app';
};

const Logo: React.FC<{ className?: string; to: string }> = ({ className = 'ds-logo', to }) => (
  <Link to={to} className={className} aria-label="Infiano">
    <img src="/images/infiano.png" alt="Infiano" className="ds-logo__img logo-image" />
  </Link>
);

interface Crumb {
  group: string;
  page: string;
}

const ADMIN_TAB_LABELS: Record<string, string> = {
  users: 'Users',
  usage: 'Usage',
  billing: 'Usage & Billing',
  connector: 'Connector',
  nlt: 'NLT Configs',
  leaderboard: 'Leaderboard',
};

const crumbsFor = (pathname: string, search: string, isAuthenticated: boolean): Crumb => {
  if (pathname.startsWith('/admin/fine-tuning-graph')) return { group: 'Admin', page: 'Fine-tuning graph' };
  if (pathname.startsWith('/admin')) {
    const tab = new URLSearchParams(search).get('tab') || 'users';
    return { group: 'Admin', page: ADMIN_TAB_LABELS[tab] || 'Users' };
  }
  if (pathname.startsWith('/connector')) return { group: 'Tuner', page: 'Connector' };
  if (pathname.startsWith('/datasets')) return { group: 'Tuner', page: 'Datasets' };
  if (pathname.startsWith('/profile')) return { group: 'Account', page: 'Profile' };
  if (pathname.startsWith('/multi-agent') || (pathname === '/' && !isAuthenticated)) {
    return { group: 'Tuner', page: 'Multi-Agent' };
  }
  return { group: 'Tuner', page: '' };
};

const initials = (name?: string, email?: string): string => {
  const src = (name && name.trim()) || (email && email.split('@')[0]) || '';
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase() || '?';
};

interface AppShellProps {
  children: React.ReactNode;
  onLoginClick: () => void;
}

const AppShell: React.FC<AppShellProps> = ({ children, onLoginClick }) => {
  const { isAuthenticated, isLoading, user, logout } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [sideOpen, setSideOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const sideCloseRef = useRef<HTMLButtonElement>(null);

  const isAdmin = useIsAdmin();
  const crumb = crumbsFor(location.pathname, location.search, isAuthenticated);

  // Белый фон на body вместо легаси-градиента, пока живёт main.css.
  useEffect(() => {
    document.body.classList.add('ds-body');
    return () => document.body.classList.remove('ds-body');
  }, []);

  // Сайдбар на мобильном закрывается при переходе.
  useEffect(() => {
    setSideOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Esc закрывает мобильный drawer и меню пользователя.
  useEffect(() => {
    if (!sideOpen && !userMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setSideOpen(false);
      setUserMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sideOpen, userMenuOpen]);

  // Открытый drawer: страница под ним не скроллится, фокус уходит в drawer,
  // после закрытия возвращается на бургер.
  useEffect(() => {
    if (!sideOpen) return;
    const burger = burgerRef.current;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    sideCloseRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      burger?.focus();
    };
  }, [sideOpen]);

  // Меню пользователя закрывается кликом снаружи.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!userMenuRef.current?.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [userMenuOpen]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      const {
        isOptimizing,
        multiAgentIsOptimizing,
        setIsOptimizing,
        setMultiAgentIsOptimizing,
        setOptimizationProgress,
        setMultiAgentOptimizationProgress,
      } = useOptimizationStore.getState();
      if (isOptimizing || multiAgentIsOptimizing) {
        unifiedWebSocketService.disconnect(true);
        setIsOptimizing(false);
        setMultiAgentIsOptimizing(false);
        setOptimizationProgress(0);
        setMultiAgentOptimizationProgress(0);
      }
      await logout();
      setUserMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      setUserMenuOpen(false);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const guest = !isLoading && !isAuthenticated;
  const shellClass = ['ds-shell', guest ? 'ds-shell--guest' : ''].filter(Boolean).join(' ');

  return (
    <div className={shellClass}>
      {!guest && sideOpen && <div className="ds-side-backdrop" aria-hidden="true" onClick={() => setSideOpen(false)} />}
      {!guest && (
        <aside className={`ds-side${sideOpen ? ' open' : ''}`} id="sidebar" aria-label="Main navigation">
          <div className="ds-side__top">
            <Logo to={isAuthenticated ? '/connector' : '/'} />
            <button
              type="button"
              className="ds-btn ds-btn--ghost ds-btn--icon ds-btn--sm ds-side__close"
              ref={sideCloseRef}
              onClick={() => setSideOpen(false)}
              aria-label="Close menu"
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
          <nav className="ds-nav">
            <div className="ds-nav__group">Tuner</div>
            <NavLink to="/multi-agent" className={({ isActive }) => `ds-nav__link${isActive ? ' active' : ''}`}>
              <i className="fas fa-project-diagram" aria-hidden="true" />
              Multi-Agent
            </NavLink>
            <NavLink to="/connector" className={({ isActive }) => `ds-nav__link${isActive ? ' active' : ''}`}>
              <i className="fas fa-plug" aria-hidden="true" />
              Connector
            </NavLink>
            <NavLink to="/datasets" className={({ isActive }) => `ds-nav__link${isActive ? ' active' : ''}`}>
              <i className="fas fa-database" aria-hidden="true" />
              Datasets
            </NavLink>

            <div className="ds-nav__group">Account</div>
            <NavLink to="/profile" className={({ isActive }) => `ds-nav__link${isActive ? ' active' : ''}`}>
              <i className="fas fa-user" aria-hidden="true" />
              Profile
            </NavLink>

            {isAdmin && (
              <>
                <div className="ds-nav__group">Admin</div>
                <NavLink to="/admin" end className={({ isActive }) => `ds-nav__link${isActive ? ' active' : ''}`}>
                  <i className="fas fa-shield-alt" aria-hidden="true" />
                  Admin
                </NavLink>
                <NavLink
                  to="/admin/fine-tuning-graph"
                  className={({ isActive }) => `ds-nav__link${isActive ? ' active' : ''}`}
                >
                  <i className="fas fa-chart-line" aria-hidden="true" />
                  Fine-tuning graph
                </NavLink>
              </>
            )}
          </nav>

          <div className="ds-side__foot">
            <a className="ds-nav__link" href={getChatUrl()} target="_blank" rel="noopener noreferrer">
              <i className="fas fa-comment-dots" aria-hidden="true" />
              InfianoChat
              <i className="fas fa-arrow-up-right-from-square ds-nav__ext" aria-hidden="true" />
            </a>
            {isAuthenticated && user && (
              <div className="ds-user" ref={userMenuRef}>
                <button
                  type="button"
                  className="ds-user-btn"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                >
                  <span className="ds-avatar">{initials(user.name, user.email)}</span>
                  <div>
                    <b>{user.name || user.email}</b>
                    <small>{isAdmin ? 'Admin' : user.email}</small>
                  </div>
                  <i className="fas fa-chevron-up ds-user-btn__chev" aria-hidden="true" />
                </button>
                {userMenuOpen && (
                  <div className="ds-menu ds-user-menu" role="menu">
                    <div className="ds-menu__head">
                      <b>{user.name || user.email}</b>
                      {user.name ? <small>{user.email}</small> : null}
                    </div>
                    <div className="ds-menu__sep" />
                    <button
                      type="button"
                      className="ds-menu__item"
                      role="menuitem"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate('/profile');
                      }}
                    >
                      <i className="fas fa-user" aria-hidden="true" />
                      Profile
                    </button>
                    <div className="ds-menu__sep" />
                    <button
                      type="button"
                      className="ds-menu__item ds-menu__item--danger"
                      role="menuitem"
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                    >
                      {isLoggingOut ? (
                        <span className="ds-spinner" aria-hidden="true" />
                      ) : (
                        <i className="fas fa-arrow-right-from-bracket" aria-hidden="true" />
                      )}
                      {isLoggingOut ? 'Logging out...' : 'Log out'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      )}

      <div className="ds-main">
        <header className="ds-topbar">
          {!guest && (
            <button
              type="button"
              className="ds-btn ds-btn--ghost ds-btn--icon ds-btn--sm ds-burger"
              ref={burgerRef}
              onClick={() => setSideOpen((v) => !v)}
              aria-label="Open menu"
              aria-expanded={sideOpen}
              aria-controls="sidebar"
            >
              <i className="fas fa-bars" aria-hidden="true" />
            </button>
          )}
          <Logo className="ds-logo ds-topbar__logo" to={isAuthenticated ? '/connector' : '/'} />
          <span className="ds-crumbs">
            {crumb.group}
            {crumb.page && (
              <>
                <span>/</span>
                <b>{crumb.page}</b>
              </>
            )}
          </span>
          <div className="ds-topbar__right">
            {guest && (
              <>
                <a
                  className="ds-btn ds-btn--ghost ds-btn--sm ds-topbar__chat"
                  href={getChatUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-comment-dots" aria-hidden="true" />
                  InfianoChat
                </a>
                <button type="button" className="ds-btn ds-btn--primary ds-btn--sm ds-signin" onClick={onLoginClick}>
                  Sign in
                </button>
              </>
            )}
          </div>
        </header>
        <main className="ds-content">{children}</main>
      </div>
    </div>
  );
};

export default AppShell;
